import styled from "@emotion/styled";
import type { NFTSingleOrder } from "@stepfinance/nft";
import type { StepColumnsType } from "@stepfinance/react";
import {
  buildImageURL,
  CheckboxFilter,
  formatBalance,
  getColumnSearchProps,
  greenTag,
  IconTextContainer,
  makeSimpleFilters,
  PlatformIcon,
  redTag,
  StepTable,
  TagInfo,
  TokenIconText,
  URLIcon,
} from "@stepfinance/react";
import type { TablePaginationConfig } from "antd";
import type { FilterValue } from "antd/lib/table/interface";
import type { FC } from "react";
import { useEffect, useState } from "react";

import { useLanguageContext } from "../../../contexts/language/language";
import { isPaginationHidden, useLocalStorageState } from "../../../utils/utils";
import { PAGE_SIZE_OPTIONS } from "../../shared";
import CellValue from "../../Table/CellValue";
import { ChevronCollapse } from "../ChevronCollapse/ChevronCollapse";

type NFTSingleOrdersTableProps = {
  totalValue: number;
  singleOrders: NFTSingleOrder[];
};

export const NFTSingleOrdersTable: FC<NFTSingleOrdersTableProps> = ({ singleOrders, totalValue }) => {
  const {
    currentLanguageData: {
      common,
      pages: {
        nftGallery: { nftValue, nftFloorPrice },
        dashboard: {
          modules: { DEXModule, nftMarkets },
        },
      },
    },
  } = useLanguageContext();

  const [pagination, setPagination] = useState<TablePaginationConfig>({});
  const [filteredInfo, setFilteredInfo] = useState<Record<string, FilterValue | null>>({});
  const [paginationSetting, setPaginationSetting] = useLocalStorageState(
    "starAtlasOpenOrdersPaginationSizeSetting",
    PAGE_SIZE_OPTIONS.m[0],
  );

  useEffect(() => {
    setPagination({
      hideOnSinglePage: isPaginationHidden(singleOrders.length, PAGE_SIZE_OPTIONS.m),
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS.m,
      pageSize: parseInt(paginationSetting),
    });
  }, [singleOrders.length, paginationSetting]);

  const columns: StepColumnsType<NFTSingleOrder> = [
    {
      title: common.platform,
      dataIndex: "platform",
      key: "platform",
      filters: makeSimpleFilters(singleOrders.map((order) => order.platform)),
      filterDropdown: (props) => <CheckboxFilter {...props} />,
      onFilter: (value, record) => record.platform.startsWith(value as string),
      filteredValue: filteredInfo.kind ?? null,
      render(_, { platform }) {
        return (
          <IconTextContainer>
            <PlatformIcon names={[platform]} />
            <TokenIconText bold>{platform}</TokenIconText>
          </IconTextContainer>
        );
      },
      sorter: (a, b) => a.platform.localeCompare(b.platform),
    },
    {
      title: "NFT",
      dataIndex: "nftName",
      key: "nft",
      className: "column-string",
      render(_, record) {
        const resizedImageUrl = buildImageURL(record.nftImageURL ?? "", "32");
        return (
          <IconTextContainer>
            <URLIcon src={resizedImageUrl} />
            <TokenIconText bold>{record.nftName}</TokenIconText>
          </IconTextContainer>
        );
      },
      ...getColumnSearchProps("nftName", "nftName"),
      filteredValue: filteredInfo.nftName ?? null,
      sorter: (a, b) => {
        return a.nftName.localeCompare(b.nftName);
      },
    },
    {
      title: common.side,
      dataIndex: "side",
      key: "side",
      className: "column-center",
      render: (side) => {
        const tagColor = side === "buy" ? greenTag : redTag;
        const tagText = side === "buy" ? DEXModule.positionTypes.buy : DEXModule.positionTypes.sell;

        return (
          <div>
            <TagInfo useSpaceMono={false} tagType="text" tagColorOverride={tagColor}>
              {tagText}
            </TagInfo>
          </div>
        );
      },
      width: 110,
      filters: [
        {
          text: "Buy",
          value: "buy",
        },
        {
          text: "Sell",
          value: "sell",
        },
      ],
      filterDropdown: (props) => <CheckboxFilter {...props} />,
      onFilter: (value, record) => record.side.startsWith(value as "buy" | "sell"),
      filteredValue: filteredInfo.side ?? null,
    },
    {
      title: nftFloorPrice,
      dataIndex: "floorPrice",
      key: "floorPrice",
      className: "column-string",
      render(_, record) {
        return record.floorPrice > 0 ? (
          <CellValue cellType="number">{formatBalance(record.floorPrice, 2)}</CellValue>
        ) : (
          <CellValue cellType="userPositionValue">{record.floorPrice}</CellValue>
        );
      },
      sorter: (a, b) => a.size - b.size,
    },
    {
      title: nftMarkets.priceSol,
      dataIndex: "size",
      key: "size",
      className: "column-string",
      render(_, record) {
        return <CellValue cellType="number">{record.size}</CellValue>;
      },
      sorter: (a, b) => a.size - b.size,
    },
    {
      title: nftValue,
      dataIndex: "value",
      key: "value",
      className: "column-string",
      render(_, record) {
        return (
          <CellValue cellType="userPositionValue" fontWeight="bold">
            {record.value}
          </CellValue>
        );
      },
      sorter: (a, b) => a.value - b.value,
    },
  ];

  return (
    <NFTOrdersContainer>
      <ChevronCollapse title={nftMarkets.nftSingleOrders} value={totalValue} isCollapseChild>
        <StepTable
          rowKey={(record, index) => `${record.nftName}-${index}`}
          onChange={(pagination, filters) => {
            setPaginationSetting(pagination.pageSize ? pagination.pageSize.toString() : PAGE_SIZE_OPTIONS.m[0]);
            setFilteredInfo(filters);
          }}
          pagination={pagination}
          columns={columns}
          dataSource={singleOrders}
        />
      </ChevronCollapse>
    </NFTOrdersContainer>
  );
};

const NFTOrdersContainer = styled.div`
  margin-top: 20px;
`;
